import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  ArrowLeftRight, 
  Search, 
  ChevronLeft, 
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ChefHat,
  Package,
  CheckCircle2,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Calendar,
  User,
  FileText,
  Filter
} from 'lucide-react';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';

export const HandoverRecordsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statistics, setStatistics] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [products, setProducts] = useState([]);
  const [showAllProducts, setShowAllProducts] = useState(false);
  const [showAllComments, setShowAllComments] = useState(false);

  useEffect(() => {
    fetchRecords();
  }, [page, searchTerm, dateFrom, dateTo, selectedProduct]);

  useEffect(() => {
    fetchStatistics();
  }, [dateFrom, dateTo, selectedProduct]);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ 
        page: page.toString(), 
        limit: '20' 
      });
      if (searchTerm) params.append('search', searchTerm);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (selectedProduct) params.append('product', selectedProduct);
      
      const response = await api.get(`/handover-records?${params}`);
      setRecords(response.data.records || []);
      setTotalPages(response.data.pages || 1);
      setTotal(response.data.total || 0);
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Error fetching records:', error);
      toast.error('Chyba při načítání záznamů');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (selectedProduct) params.append('product', selectedProduct);
      
      const response = await api.get(`/handover-records/statistics?${params}`);
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'dd.MM.yyyy HH:mm', { locale: cs });
    } catch {
      return dateStr;
    }
  };

  const getDifferenceClass = (diff) => {
    if (diff > 0) return 'text-green-600';
    if (diff < 0) return 'text-red-600';
    return 'text-slate-600';
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <ArrowLeftRight className="w-7 h-7 text-purple-600" />
              Záznamy předávky
            </h1>
            <p className="text-slate-500 mt-1">
              Přehled předávek mezi kuchyní a balírnou
            </p>
          </div>
        </div>

        {/* Statistics Cards */}
        {statistics && statistics.total_handovers !== undefined && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Celkem předávek</p>
                    <p className="text-2xl font-bold text-slate-800">{statistics.total_handovers || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">S korekcí</p>
                    <p className="text-2xl font-bold text-orange-600">{statistics.with_corrections || 0}</p>
                    <p className="text-xs text-slate-400">
                      {statistics.total_handovers > 0 ? 
                        `${((statistics.with_corrections / statistics.total_handovers) * 100).toFixed(1)}%` : '0%'}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Prům. rozdíl</p>
                    <p className={`text-2xl font-bold ${getDifferenceClass(statistics.avg_difference)}`}>
                      {statistics.avg_difference > 0 ? '+' : ''}{statistics.avg_difference?.toFixed(2) || 0} kg
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Bez rozdílů</p>
                    <p className="text-2xl font-bold text-green-600">{statistics.without_corrections}</p>
                    <p className="text-xs text-slate-400">
                      {statistics.total_handovers > 0 ? 
                        `${((statistics.without_corrections / statistics.total_handovers) * 100).toFixed(1)}%` : '0%'}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Product Statistics */}
        {statistics?.product_stats?.length > 0 && (
          <Card className="mb-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-purple-600" />
                Statistiky podle výrobků
                <Badge variant="secondary" className="ml-2">
                  {statistics.product_stats.length} výrobků
                </Badge>
              </CardTitle>
              {statistics.product_stats.length > 5 && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowAllProducts(!showAllProducts)}
                  className="text-purple-600"
                >
                  {showAllProducts ? (
                    <>
                      <ChevronUp className="w-4 h-4 mr-1" />
                      Zobrazit méně
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4 mr-1" />
                      Zobrazit vše ({statistics.product_stats.length})
                    </>
                  )}
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="text-left py-3 px-2">Výrobek</th>
                      <th className="text-center py-3 px-2">Počet předávek</th>
                      <th className="text-center py-3 px-2">S korekcí</th>
                      <th className="text-right py-3 px-2">Prům. rozdíl</th>
                      <th className="text-right py-3 px-2">Trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(showAllProducts ? statistics.product_stats : statistics.product_stats.slice(0, 5)).map((stat, idx) => (
                      <tr key={idx} className="border-b hover:bg-slate-50">
                        <td className="py-3 px-2 font-medium">{stat.product_name}</td>
                        <td className="text-center py-3 px-2">{stat.count}</td>
                        <td className="text-center py-3 px-2">
                          <span className={stat.with_corrections > 0 ? 'text-orange-600' : 'text-green-600'}>
                            {stat.with_corrections}
                          </span>
                        </td>
                        <td className={`text-right py-3 px-2 font-medium ${getDifferenceClass(stat.avg_difference)}`}>
                          {stat.avg_difference > 0 ? '+' : ''}{stat.avg_difference?.toFixed(2) || 0} kg
                        </td>
                        <td className="text-right py-3 px-2">
                          {stat.avg_difference > 0 ? (
                            <TrendingUp className="w-4 h-4 text-green-500 inline" />
                          ) : stat.avg_difference < 0 ? (
                            <TrendingDown className="w-4 h-4 text-red-500 inline" />
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Shortage Statistics - Production Completion */}
        {statistics?.shortage_stats && statistics.shortage_stats.total_with_shortage > 0 && (
          <Card className="mb-6 border-red-200">
            <CardHeader className="bg-red-50 border-b border-red-100">
              <CardTitle className="text-lg flex items-center gap-2 text-red-700">
                <TrendingDown className="w-5 h-5" />
                Statistiky méně odvedeného množství
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-red-50 p-4 rounded-lg text-center">
                  <p className="text-3xl font-bold text-red-600">{statistics.shortage_stats.total_with_shortage}</p>
                  <p className="text-sm text-red-700">Celkem s méně odvedením</p>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg text-center">
                  <p className="text-3xl font-bold text-orange-600">
                    {statistics.shortage_stats.total_shortage_quantity?.toLocaleString('cs-CZ') || 0}
                  </p>
                  <p className="text-sm text-orange-700">Celkem chybějící množství</p>
                </div>
                <div className="bg-amber-50 p-4 rounded-lg text-center">
                  <p className="text-3xl font-bold text-amber-600">
                    {statistics.shortage_stats.total_with_shortage > 0 
                      ? (statistics.shortage_stats.total_shortage_quantity / statistics.shortage_stats.total_with_shortage).toFixed(1)
                      : 0}
                  </p>
                  <p className="text-sm text-amber-700">Prům. chybějící množství</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg text-center">
                  <p className="text-3xl font-bold text-purple-600">
                    {statistics.total_handovers > 0 
                      ? ((statistics.shortage_stats.total_with_shortage / statistics.total_handovers) * 100).toFixed(1)
                      : 0}%
                  </p>
                  <p className="text-sm text-purple-700">Podíl s méně odvedením</p>
                </div>
              </div>
              
              {/* Reasons breakdown */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-slate-600 mb-3">Co nevyšlo - přehled důvodů</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(statistics.shortage_stats.reasons).map(([key, data]) => (
                    <div key={key} className={`p-3 rounded-lg border ${
                      data.count > 0 ? 'bg-white border-red-200' : 'bg-slate-50 border-slate-200'
                    }`}>
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-slate-700">{data.label}</span>
                        <Badge variant={data.count > 0 ? "destructive" : "secondary"}>
                          {data.count}×
                        </Badge>
                      </div>
                      {data.count > 0 && (
                        <p className="text-xs text-slate-500 mt-1">
                          Celkem: -{data.total_diff?.toLocaleString('cs-CZ') || 0}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Recent comments */}
              {statistics.shortage_stats.recent_comments?.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-slate-600">
                      Poslední komentáře k méně odvedení
                      <Badge variant="secondary" className="ml-2">
                        {statistics.shortage_stats.recent_comments.length}
                      </Badge>
                    </h4>
                    {statistics.shortage_stats.recent_comments.length > 5 && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setShowAllComments(!showAllComments)}
                        className="text-red-600"
                      >
                        {showAllComments ? (
                          <>
                            <ChevronUp className="w-4 h-4 mr-1" />
                            Zobrazit méně
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-4 h-4 mr-1" />
                            Zobrazit vše ({statistics.shortage_stats.recent_comments.length})
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                  <div className="space-y-2">
                    {(showAllComments 
                      ? statistics.shortage_stats.recent_comments 
                      : statistics.shortage_stats.recent_comments.slice(0, 5)
                    ).map((item, idx) => (
                      <div key={idx} className="bg-slate-50 p-3 rounded-lg border">
                        <div className="flex justify-between items-start mb-1">
                          <span className="font-medium text-slate-800">{item.doklad}</span>
                          <span className="text-xs text-slate-500">
                            {item.date ? formatDate(item.date) : '-'}
                          </span>
                        </div>
                        <div className="flex gap-2 mb-2 flex-wrap">
                          {item.reasons?.map((reason, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              {reason === 'omacka' && 'Omáčka'}
                              {reason === 'maso' && 'Maso'}
                              {reason === 'pevna_slozka' && 'Pevná složka'}
                              {reason === 'jine' && (item.reason_other || 'Jiné')}
                            </Badge>
                          ))}
                          <Badge variant="destructive" className="text-xs">
                            -{item.diff?.toLocaleString('cs-CZ') || 0}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-600 italic">"{item.comment}"</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="relative md:col-span-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Hledat podle výrobku, šarže..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                  className="pl-10"
                />
              </div>
              <div>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                  placeholder="Od data"
                />
              </div>
              <div>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                  placeholder="Do data"
                />
              </div>
              <div>
                <Select value={selectedProduct || "all"} onValueChange={(v) => { setSelectedProduct(v === "all" ? "" : v); setPage(1); }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Všechny výrobky" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Všechny výrobky</SelectItem>
                    {products.map((p, idx) => (
                      <SelectItem key={idx} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Records List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : records.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <ArrowLeftRight className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">
                Žádné záznamy předávky
              </h3>
              <p className="text-slate-400">
                Záznamy se zde zobrazí po potvrzení předávek z kuchyně
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="space-y-3">
              {records.map((record) => (
                <Card key={record.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                      {/* Product info */}
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-800">
                          {record.product_name || 'Neznámý výrobek'}
                        </h3>
                        <p className="text-sm text-slate-500">
                          Šarže: {record.batch_number || '-'} | Doklad: {record.doklad || '-'}
                        </p>
                      </div>
                      
                      {/* Handover info */}
                      <div className="flex flex-col md:flex-row gap-4 text-sm">
                        <div className="flex items-center gap-2 text-orange-700">
                          <ChefHat className="w-4 h-4" />
                          <span>{record.confirmed_by_name}</span>
                          <span className="text-slate-400 text-xs">
                            {formatDate(record.confirmed_at)}
                          </span>
                        </div>
                        
                        {record.balirna_verified && (
                          <div className="flex items-center gap-2 text-blue-700">
                            <Package className="w-4 h-4" />
                            <span>{record.balirna_verified_by_name}</span>
                            <span className="text-slate-400 text-xs">
                              {formatDate(record.balirna_verified_at)}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {/* Status badge */}
                      {record.has_corrections ? (
                        <Badge className="bg-orange-100 text-orange-700 border-orange-200">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          Korekce
                        </Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-700 border-green-200">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          OK
                        </Badge>
                      )}
                    </div>
                    
                    {/* Items detail */}
                    <div className="mt-4 pt-4 border-t">
                      {/* Completion data - Production quantities */}
                      {record.completion_data?.actual_quantity !== undefined && (
                        <div className="mb-4 p-3 bg-slate-50 rounded-lg">
                          <h4 className="text-sm font-medium text-slate-600 mb-2 flex items-center gap-2">
                            <BarChart3 className="w-4 h-4" />
                            Odvedení výroby
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-slate-500">Plánované:</span>
                              <span className="ml-2 font-medium">{record.completion_data.planned_quantity?.toLocaleString('cs-CZ')} {record.unit || 'ks'}</span>
                            </div>
                            <div>
                              <span className="text-slate-500">Skutečné:</span>
                              <span className={`ml-2 font-medium ${record.completion_data.actual_quantity < record.completion_data.planned_quantity ? 'text-red-600' : 'text-green-600'}`}>
                                {record.completion_data.actual_quantity?.toLocaleString('cs-CZ')} {record.unit || 'ks'}
                              </span>
                            </div>
                            <div>
                              <span className="text-slate-500">Rozdíl:</span>
                              <span className={`ml-2 font-medium ${(record.completion_data.actual_quantity - record.completion_data.planned_quantity) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                {(record.completion_data.actual_quantity - record.completion_data.planned_quantity) > 0 ? '+' : ''}
                                {(record.completion_data.actual_quantity - record.completion_data.planned_quantity)?.toLocaleString('cs-CZ')} {record.unit || 'ks'}
                              </span>
                            </div>
                            {record.completion_data.shortage_reasons && record.completion_data.shortage_reasons.length > 0 && (
                              <div>
                                <span className="text-slate-500">Nevyšlo:</span>
                                <span className="ml-2 font-medium text-orange-600">
                                  {record.completion_data.shortage_reasons.map(reason => {
                                    if (reason === 'omacka') return 'Omáčka';
                                    if (reason === 'maso') return 'Maso';
                                    if (reason === 'pevna_slozka') return 'Pevná složka';
                                    if (reason === 'jine') return record.completion_data.shortage_reason_other;
                                    return reason;
                                  }).join(', ')}
                                </span>
                              </div>
                            )}
                          </div>
                          {record.completion_data.shortage_comment && (
                            <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-sm">
                              <span className="text-orange-700 font-medium">Komentář: </span>
                              <span className="text-slate-700">{record.completion_data.shortage_comment}</span>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Kitchen items */}
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                        {record.items?.map((item, idx) => {
                          const correction = record.corrections?.find(c => c.item_id === item.item_id);
                          const diff = correction ? correction.received_quantity - correction.original_quantity : 0;
                          return (
                            <div key={idx} className={`p-2 rounded text-sm ${diff !== 0 ? 'bg-orange-50 border border-orange-200' : 'bg-slate-50'}`}>
                              <p className="text-slate-600 text-xs">{item.name}</p>
                              <p className="font-medium">
                                {correction ? correction.received_quantity : item.quantity} {item.unit}
                              </p>
                              {diff !== 0 && (
                                <p className={`text-xs ${getDifferenceClass(diff)}`}>
                                  ({diff > 0 ? '+' : ''}{diff.toFixed(2)})
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Předchozí
                </Button>
                
                <span className="px-4 text-sm text-slate-600">
                  Strana {page} z {totalPages}
                </span>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Další
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default HandoverRecordsPage;
